import { HTMLAttributes, ReactNode, type KeyboardEvent as ReactKeyboardEvent } from "react";
import cn from "clsx";

// DataTable — DS-Tabellen-Bausteine (Grid-Spalten, uppercase NUR im thead,
// Row-Hover 4% Petrol, Fokusring inset). Konsumiert nur Tokens.
//   <DataTable>  = Karten-Huelle (Border/Radius/overflow).
//   <Thead cols> = Kopfzeile (Grid, bg-surface-2, border-b).
//   <Th>         = einzelne Kopf-Zelle (uppercase, text-3).
//   <Trow cols>  = Datenzeile (Grid, Hover/Fokus/Tastatur bei onClick).

export interface DataTableProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}
export function DataTable({ className, children, ...rest }: DataTableProps) {
  return (
    <div className={cn("bg-surface border border-border rounded-card overflow-hidden", className)} {...rest}>
      {children}
    </div>
  );
}

export interface TheadProps {
  cols: string;
  className?: string;
  children: ReactNode;
}
export function Thead({ cols, className, children }: TheadProps) {
  return (
    <div
      className={cn("grid items-center gap-3.5 px-[18px] py-2.5 border-b border-border bg-surface-2", className)}
      style={{ gridTemplateColumns: cols }}
    >
      {children}
    </div>
  );
}

export interface ThProps {
  align?: "left" | "right" | "center";
  className?: string;
  children?: ReactNode;
}
export function Th({ align = "left", className, children }: ThProps) {
  return (
    <span
      className={cn(
        "text-[0.68rem] font-semibold uppercase tracking-wide text-text-3",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
    >
      {children}
    </span>
  );
}

export interface TrowProps extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  cols: string;
  onClick?: () => void;
  /** aria-label fuer die klickbare Zeile (Screenreader). */
  label?: string;
  children: ReactNode;
}
export function Trow({ cols, onClick, label, className, children, ...rest }: TrowProps) {
  const clickable = !!onClick;
  const activate = clickable
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": label,
        onClick,
        onKeyDown: (e: ReactKeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick!();
          }
        },
      }
    : {};
  return (
    <div
      {...activate}
      className={cn(
        "grid items-center gap-3.5 px-[18px] py-2.5 border-t border-border first:border-t-0",
        clickable &&
          "cursor-pointer hover:bg-accent-500/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500",
        className,
      )}
      style={{ gridTemplateColumns: cols }}
      {...rest}
    >
      {children}
    </div>
  );
}
